import React from "react";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import { getFirebaseApp } from "./firebase";

// Get storage instance from the already-initialized Firebase app
function getStorageInstance() {
  const app = getFirebaseApp();
  if (!app) {
    throw new Error("Firebase is not configured");
  }
  return getStorage(app);
}

// ==================== URL Cache ====================
// In-memory cache for game icon URLs to reduce Firebase API calls
const urlCache = new Map<string, { url: string; timestamp: number }>();
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Get cached URL if available and not expired
 */
function getCachedUrl(gameKey: string): string | null {
  const cached = urlCache.get(gameKey);
  if (cached) {
    const isExpired = Date.now() - cached.timestamp > CACHE_DURATION_MS;
    if (!isExpired) {
      return cached.url;
    } else {
      // Remove expired cache
      urlCache.delete(gameKey);
    }
  }
  return null;
}

/**
 * Store URL in cache
 */
function setCachedUrl(gameKey: string, url: string): void {
  urlCache.set(gameKey, { url, timestamp: Date.now() });
}

/**
 * Get download URL for a game icon from Firebase Storage
 * @param gameKey - The game key (e.g., "AFKJOURNEY")
 * @returns Promise<string> - The download URL for the game icon
 */
export async function getGameIconUrl(gameKey: string): Promise<string> {
  try {
    // Check cache first
    const cachedUrl = getCachedUrl(gameKey);
    if (cachedUrl) {
      return cachedUrl;
    }

    const storage = getStorageInstance();
    const iconRef = ref(storage, `game-icons/${gameKey.toLowerCase()}.jpg`);
    const url = await getDownloadURL(iconRef);
    
    // Cache the URL
    setCachedUrl(gameKey, url);
    
    return url;
  } catch (error) {
    console.error(`Failed to get icon URL for game ${gameKey}:`, error);
    // Return empty string on error, component will use fallback
    return "";
  }
}

/**
 * Get download URLs for multiple game icons
 * @param gameKeys - Array of game keys
 * @returns Promise<Record<string, string>> - Map of gameKey to URL
 */
export async function getGameIconUrls(
  gameKeys: string[]
): Promise<Record<string, string>> {
  const urls: Record<string, string> = {};

  const promises = gameKeys.map(async (gameKey) => {
    const url = await getGameIconUrl(gameKey);
    urls[gameKey] = url;
  });

  await Promise.all(promises);
  return urls;
}

/**
 * Hook to load game icon URL with lazy loading support
 * Returns the URL, loading state, and error
 */
export function useGameIconUrl(gameKey: string | undefined, lazy = false) {
  const [url, setUrl] = React.useState<string>("");
  const [loading, setLoading] = React.useState<boolean>(!lazy);
  const [error, setError] = React.useState<Error | null>(null);
  const [isInView, setIsInView] = React.useState<boolean>(!lazy);

  // Intersection Observer for lazy loading
  const elementRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!lazy) {
      // No lazy loading, load immediately
      if (!gameKey) {
        setUrl("");
        setLoading(false);
        return;
      }

      setLoading(true);
      getGameIconUrl(gameKey)
        .then(setUrl)
        .catch((err) => {
          setError(err);
          setUrl("");
        })
        .finally(() => setLoading(false));
    } else {
      // Setup intersection observer for lazy loading
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !isInView) {
            setIsInView(true);
            observer.unobserve(entry.target);
          }
        },
        { rootMargin: "50px" } // Start loading 50px before element enters viewport
      );

      if (elementRef.current) {
        observer.observe(elementRef.current);
      }

      return () => {
        if (elementRef.current) {
          observer.unobserve(elementRef.current);
        }
      };
    }
  }, [gameKey, lazy, isInView]);

  // Load image when in view (for lazy loading)
  React.useEffect(() => {
    if (lazy && isInView && gameKey) {
      setLoading(true);
      getGameIconUrl(gameKey)
        .then(setUrl)
        .catch((err) => {
          setError(err);
          setUrl("");
        })
        .finally(() => setLoading(false));
    }
  }, [gameKey, lazy, isInView]);

  return { url, loading, error, elementRef };
}

/**
 * Hook for batch loading multiple game icons
 * Useful for loading icons for a list of games
 */
export function useGameIconUrls(gameKeys: string[] | undefined) {
  const [urls, setUrls] = React.useState<Record<string, string>>({});
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    if (!gameKeys || gameKeys.length === 0) {
      setUrls({});
      setLoading(false);
      return;
    }

    setLoading(true);
    getGameIconUrls(gameKeys)
      .then(setUrls)
      .catch((err) => {
        setError(err);
        setUrls({});
      })
      .finally(() => setLoading(false));
  }, [gameKeys]);

  return { urls, loading, error };
}

/**
 * Clear the URL cache (useful for testing or manual refresh)
 */
export function clearUrlCache(): void {
  urlCache.clear();
}

/**
 * Get cache statistics for debugging
 */
export function getCacheStats(): { size: number; entries: string[] } {
  return {
    size: urlCache.size,
    entries: Array.from(urlCache.keys()),
  };
}

/**
 * Export storage instance getter for direct access if needed
 */
export { getStorageInstance as storage };
